"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import AvatarImage from "@/components/AvatarImage";
import {
  LogOut,
  LayoutDashboard,
  RefreshCcw,
  ChevronDown,
  UserPlus,
  Loader,
  Check,
  UserCircle,
} from "lucide-react";
import NotificationDropdown from "@/components/NotificationDropdown";
import { useTranslations } from "next-intl";
import { type AuthUser } from "@/modules/auth/types";
import LoginLink from "@/modules/auth/components/LoginLink";

type AuthSectionProps = {
  loading: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  userName: string;
  localizedHref: (path: string) => string;
  switchableAccounts: { id: string; name?: string | null; isParent?: boolean }[];
  switchingDependantId: string | null;
  handleDependantSwitch: (id: string) => void;
  canCreateDependant: boolean;
  handleSwitchAccountNavigation: (target: "list" | "create") => void;
  onAddDependant: () => void;
  handleLogout: () => void;
  /**
   * Every Teacher also has a Student profile, so the family/dependant
   * "Switch Profile" feature must be hidden for Teachers entirely, keyed
   * off of whether the account IS a Teacher rather than which profile is
   * currently active.
   */
  isTeacherAccount: boolean;
};

export default function NavbarAuthSection({
  loading,
  isAuthenticated,
  user,
  userName,
  localizedHref,
  switchableAccounts,
  switchingDependantId,
  handleDependantSwitch,
  canCreateDependant,
  handleSwitchAccountNavigation,
  onAddDependant,
  handleLogout,
  isTeacherAccount,
}: AuthSectionProps) {
  const t = useTranslations("web.navbar");

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSwitchOpen, setIsSwitchOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsProfileOpen(false);
        setIsSwitchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const closeMenu = () => {
    setIsProfileOpen(false);
    setIsSwitchOpen(false);
  };

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-slate-100 animate-pulse" />
        <div className="w-9 h-9 rounded-full bg-slate-100 animate-pulse" />
        <div className="w-28 h-4 bg-slate-100 rounded-full animate-pulse" />
      </div>
    );
  }

  /* ── Authenticated ── */
  if (isAuthenticated) {
    const accountType = user?.account_type === "teacher" ? "teacher" : "student";

    return (
      <>
        <NotificationDropdown />

        {/* ── Trigger ── */}
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            onClick={() => setIsProfileOpen((prev) => !prev)}
            className="flex items-center gap-2.5 cursor-pointer focus:outline-none select-none"
          >
            <div className="relative flex-shrink-0">
              <AvatarImage
                src={user?.image as string | undefined}
                alt={userName}
                width={38}
                height={38}
                className="rounded-full object-cover ring-2 ring-secondary/40 w-[38px] h-[38px]"
              />
              <span
                className="absolute bottom-0 end-0 w-2.5 h-2.5 rounded-full border-2 border-white"
                style={{ background: "#22c55e" }}
              />
            </div>

            <div className="flex flex-col items-start leading-none">
              <span className="text-[14px] font-bold text-nav-text max-w-[120px] truncate">
                {userName}
              </span>
              <span className="text-[11px] font-medium text-secondary mt-0.5 capitalize">
                {t(accountType === "teacher" ? "role_teacher" : "role_student")}
              </span>
            </div>

            <ChevronDown
              size={15}
              className={`text-secondary flex-shrink-0 ms-0.5 transition-transform duration-200 ${isProfileOpen ? "rotate-180" : ""}`}
            />
          </button>

          {/* ── Dropdown panel ── */}
          {isProfileOpen && (
            <div
              className="absolute end-0 top-full z-[100] mt-2.5 w-[260px] rounded-[10px] bg-white"
              style={{ border: "1px solid color-mix(in oklab, var(--nav-text) 10%, transparent)" }}
            >
              <div className="p-2 flex flex-col gap-0.5">
                {/* Dashboard */}
                <Link
                  href={localizedHref("/dashboard")}
                  onClick={closeMenu}
                  className="flex items-center gap-3 rounded-[10px] px-3 py-2 !text-nav-text font-semibold hover:bg-nav-text/5 transition-colors duration-150"
                  style={{ fontSize: "13.5px" }}
                >
                  <LayoutDashboard
                    size={15}
                    className="text-secondary fill-secondary shrink-0"
                  />
                  {t("dashboard")}
                </Link>

                {/* My Profile */}
                <Link
                  href={localizedHref(`/${accountType}/profile`)}
                  onClick={closeMenu}
                  className="flex items-center gap-3 rounded-[10px] px-3 py-2 !text-nav-text font-semibold hover:bg-nav-text/5 transition-colors duration-150"
                  style={{ fontSize: "13.5px" }}
                >
                  <UserCircle size={15} className="text-secondary shrink-0" />
                  {t("profile")}
                </Link>

                {/* Switch account — inline submenu (hidden for Teachers) */}
                {!isTeacherAccount && (
                <div>
                  <button
                    type="button"
                    onClick={() => setIsSwitchOpen((prev) => !prev)}
                    className="w-full flex items-center justify-between gap-2 rounded-[10px] px-2.5 py-1.5 !text-nav-text font-semibold cursor-pointer hover:bg-nav-text/5 transition-colors duration-150"
                    style={{ fontSize: "13.5px" }}
                  >
                    <span className="flex items-center gap-2.5">
                      <RefreshCcw size={15} className="text-secondary shrink-0" />
                      {t("switch_account")}
                    </span>
                    <ChevronDown
                      size={13}
                      className={`text-secondary flex-shrink-0 transition-transform duration-200 ${isSwitchOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isSwitchOpen && (
                    <div className="mt-0.5 space-y-0.5 bg-nav-text/[0.03] rounded-lg p-1 max-h-[220px] overflow-y-auto">
                      {switchableAccounts.map((account, index) => {
                        const isSwitching =
                          switchingDependantId === account.id;
                        const isBusy = switchingDependantId !== null;
                        const isActive = account.id === String(user?.id);

                        return (
                          <button
                            key={account.id}
                            type="button"
                            disabled={isBusy}
                            onClick={() =>
                              void handleDependantSwitch(account.id)
                            }
                            className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-start transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer ${
                              isActive ? "bg-white" : "hover:bg-white"
                            }`}
                          >
                            <div className="flex-1 min-w-0 flex items-center gap-1.5">
                              <p
                                className={`truncate leading-tight ${isActive ? "font-bold" : "font-semibold"}`}
                                style={{ fontSize: "12.5px", color: "var(--nav-text)" }}
                              >
                                {account.name || `${t("user")} ${index + 1}`}
                              </p>
                              {account.isParent && (
                                <span
                                  className="shrink-0 rounded-full px-1.5 py-[1px] text-[9px] font-bold"
                                  style={{ background: "color-mix(in oklab, var(--secondary) 15%, transparent)", color: "var(--secondary)" }}
                                >
                                  {t("primary")}
                                </span>
                              )}
                            </div>
                            {isSwitching ? (
                              <Loader
                                size={12}
                                className="animate-spin text-secondary shrink-0"
                              />
                            ) : isActive ? (
                              <Check size={12} className="text-secondary shrink-0" />
                            ) : null}
                          </button>
                        );
                      })}

                      {canCreateDependant && (
                        <button
                          type="button"
                          onClick={() => {
                            closeMenu();
                            onAddDependant();
                          }}
                          className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-start transition-colors duration-150 hover:bg-white cursor-pointer border border-secondary mt-0.5 text-xs font-semibold text-secondary"
                        >
                          <UserPlus size={13} className="shrink-0" />
                          <span>{t("add_new_dependant")}</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          closeMenu();
                          handleSwitchAccountNavigation("list");
                        }}
                        className="w-full flex items-center justify-center rounded-md px-2 py-1.5 text-center transition-colors duration-150 hover:bg-white cursor-pointer text-secondary mt-0.5 text-xs font-semibold"
                      >
                        {t("view_all_profiles")}
                      </button>
                    </div>
                  )}
                </div>
                )}

                <hr className="my-1 border-nav-text/[0.08]" />

                {/* Logout */}
                <button
                  type="button"
                  onClick={() => {
                    closeMenu();
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-start font-semibold cursor-pointer hover:bg-red-50 transition-colors duration-150"
                  style={{ fontSize: "13.5px", color: "#ef4444" }}
                >
                  <LogOut size={15} className="text-red shrink-0" />
                  {t("logout")}
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  /* ── Guest buttons ── */
  return (
    <LoginLink
      loginHref={localizedHref("/login")}
      className="min-w-[110px] bg-primary/10 text-primary font-semibold px-6 py-2.5 rounded-[10px] hover:bg-secondary hover:text-white transition text-center text-[14px] cursor-pointer"
    >
      {t("login")}
    </LoginLink>
  );
}
